import { getAuthToken } from './auth'
import config from './config'

const API_BASE_URL = `${config.API_BASE_URL}/api`

export interface RolePermission {
  role: 'user' | 'b2b-user' | 'admin'
  modules: string[]
  description: string
  updated_at: string
}

export interface UpdateRolePermissionRequest {
  modules: string[]
  description: string
}

function getAuthHeaders(): HeadersInit {
  let token = getAuthToken()

  if (!token) {
    try {
      const userStr = localStorage.getItem('user')
      if (userStr) {
        const userData = JSON.parse(userStr)
        token = userData?.token
      }
    } catch (e) {
      console.error('rolePermissions: Failed to parse user from localStorage', e)
    }
  }

  if (!token) {
    throw new Error('No authentication token found')
  }

  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

export async function listRolePermissions(): Promise<RolePermission[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/role-permissions`, {
      method: 'GET',
      headers: getAuthHeaders(),
    })

    if (!response.ok) {
      const err = await response
        .json()
        .catch(() => ({ message: 'Failed to list role permissions' }))
      throw new Error(err.message || err.error || 'Failed to list role permissions')
    }

    // Accept multiple shapes for backward compatibility:
    // 1) { roles: RolePermission[], success: true, total: 3 }
    // 2) { items: RolePermission[] }
    // 3) RolePermission[]
    const data = (await response.json()) as
      | { roles?: RolePermission[]; items?: RolePermission[]; success?: boolean; total?: number }
      | RolePermission[]

    if (Array.isArray(data)) return data
    if (data && Array.isArray((data as any).roles)) return (data as any).roles
    if (data && Array.isArray((data as any).items)) return (data as any).items
    return []
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(error.message)
    }
    throw new Error('Unknown error while listing role permissions')
  }
}

// Fetch effective modules for current user (non-admin)
export async function myRolePermissions(): Promise<{ role: string; modules: string[]; description: string }> {
  const res = await fetch(`${API_BASE_URL}/role-permissions/me`, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || json.success === false) {
    throw new Error(json?.message || 'Failed to load current role permissions')
  }
  return {
    role: json.role || '',
    modules: Array.isArray(json.modules) ? json.modules : [],
    description: json.description || '',
  }
}

export async function updateRolePermission(
  role: string,
  data: UpdateRolePermissionRequest
): Promise<RolePermission> {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/role-permissions/${encodeURIComponent(role)}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    })

    const payload = await response.json().catch(() => ({}))

    if (!response.ok) {
      const message = (payload && (payload.message || payload.error)) || 'Failed to update role permission'
      throw new Error(message)
    }

    const maybe = payload as RolePermission | { item?: RolePermission; data?: RolePermission }
    const entity = (maybe as any).item || (maybe as any).data || maybe
    return entity as RolePermission
  } catch (error: unknown) {
    if (error instanceof Error) {
      throw new Error(error.message)
    }
    throw new Error('Unknown error while updating role permission')
  }
}
